import React, { useState, useEffect, useMemo } from 'react';
import { 
  Plus, 
  FileText,
  Download,
  Trash2, 
  Scissors, 
  Package, 
  Columns,
  GanttChart,
  CheckCircle,
  Clock,
  AlertTriangle,
  Calendar,
  Upload,
  X,
  Filter,
  ArrowUpDown
} from 'lucide-react';
import Card, { CardHeader, CardContent } from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import { useAppStore } from '../store';
import { useAuth } from '../context/AuthContext';
import { Production, Sample, ProductionTask, SampleTask } from '../types';
import { format, addWeeks, startOfWeek, endOfWeek, eachDayOfInterval, isSameDay, isWithinInterval, parseISO } from 'date-fns';
import KanbanView from '../components/ui/KanbanView';
import GanttView from '../components/ui/GanttView';
import ProductionTaskModal from '../components/modals/ProductionTaskModal';
import BulkTaskImportModal from '../components/modals/BulkTaskImportModal';
import SampleTaskModal from '../components/modals/SampleTaskModal';
import ConfirmModal from '../components/modals/ConfirmModal';
import ErrorModal from '../components/modals/ErrorModal';
import SatisfactionSurveyLinkModal from '../components/modals/SatisfactionSurveyLinkModal';
import { createSatisfactionSurvey } from '../utils/satisfactionSurvey';
import ChangeHistoryModal from '../components/modals/ChangeHistoryModal';
import TimelineView from '../components/ui/TimelineView';
import { formatCurrency } from '../utils/formatters';
import EntityModal from '../components/modals/EntityModal';

type EntityType = 'production' | 'sample';
type ViewMode = 'calendar' | 'timeline' | 'kanban' | 'gantt';

const ProductionManager: React.FC = () => {
  const { isAdmin, userRole } = useAuth();
  const { 
    productions, 
    samples, 
    productionTasks, 
    sampleTasks, 
    suppliers, 
    clients,
    addSupplier,
    addProductionTask, 
    updateProductionTask, 
    deleteProductionTask,
    addSampleTask,
    updateSampleTask,
    deleteSampleTask,
    updateProduction,
    updateSample
  } = useAppStore();
  
  // State for entity selection
  const [entityType, setEntityType] = useState<EntityType>('production');
  const [selectedEntityId, setSelectedEntityId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // State for task management
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<ProductionTask | SampleTask | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  
  // State for change history modal
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  
  // State for view mode
  const [viewMode, setViewMode] = useState<ViewMode | 'supplier'>('calendar');

  // Estado para crear proveedores desde producción
  const [isSupplierModalOpen, setIsSupplierModalOpen] = useState(false);
  
  // Estado para los filtros de la pestaña de proveedor
  const [supplierFilter, setSupplierFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('month');
  // Estado para mostrar solo tareas atrasadas
  const [showOnlyLate, setShowOnlyLate] = useState(false);
  // Estado para filtrar por tipo de entidad
  const [entityTypeFilter, setEntityTypeFilter] = useState<'all' | 'production' | 'sample'>('all');

  // Estado para drag and drop
  const [draggedEntity, setDraggedEntity] = useState<{id: string, type: EntityType} | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOverDay, setDragOverDay] = useState<Date | null>(null);
  const [isUpdatingDate, setIsUpdatingDate] = useState(false);
  
  // Estado para marcar como terminado
  const [isMarkingAsFinished, setIsMarkingAsFinished] = useState(false);
  
  // Estado para importación masiva
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false);
  
  // Estado para modal de cumplimiento de entrega
  const [isDeliveryComplianceModalOpen, setIsDeliveryComplianceModalOpen] = useState(false);
  const [deliveryComplianceModalType, setDeliveryComplianceModalType] = useState<'production' | 'sample'>('production');
  
  
  // Estado para el período de la métrica de cumplimiento (independientes)
  const [compliancePeriod, setCompliancePeriod] = useState('60days');
  const [compliancePeriodSamples, setCompliancePeriodSamples] = useState('60days');
  
  // Estado para el orden de la tabla en el modal
  const [isAscendingOrder, setIsAscendingOrder] = useState(true);
  
  // Estado para el filtro de tipo de entidad en el modal
  const [entityTypeFilterModal, setEntityTypeFilterModal] = useState<'all' | 'production' | 'sample'>('all');
  
    // Estado para el modal de fecha de terminación
    const [isFinishDateModalOpen, setIsFinishDateModalOpen] = useState(false);
    const [entityToFinish, setEntityToFinish] = useState<{id: string, type: EntityType, name: string} | null>(null);
    const [finishDate, setFinishDate] = useState('');
    // Estado para el modal de link de encuesta de satisfacción
    const [surveyLinkModal, setSurveyLinkModal] = useState<{ token: string; entityName: string } | null>(null);
    
    // Estado para edición de fechas en el modal
    const [editingEntity, setEditingEntity] = useState<{id: string, type: EntityType, field: 'delivery' | 'finish'} | null>(null);
    const [editingValue, setEditingValue] = useState('');
  
  
  
  // Función para obtener el texto del período
  const getPeriodText = (period: string) => {
    switch (period) {
      case '7days':
        return 'últimos 7 días';
      case '30days':
        return 'últimos 30 días';
      case '60days':
        return 'últimos 60 días';
      case '90days':
        return 'últimos 90 días';
      case '6months':
        return 'últimos 6 meses';
      case 'year':
        return 'este año';
      default:
        return 'últimos 60 días';
    }
  };

  // Get client name for the selected entity
  const getClientName = (clientId: string) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.name : 'Cliente no encontrado';
  };

  // Helper para obtener startDate según período
  const getStartDateForPeriod = (period: string) => {
    const now = new Date();
    switch (period) {
      case '7days': return new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));
      case '30days': return new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
      case '60days': return new Date(now.getTime() - (60 * 24 * 60 * 60 * 1000));
      case '90days': return new Date(now.getTime() - (90 * 24 * 60 * 60 * 1000));
      case '6months': return new Date(now.getTime() - (180 * 24 * 60 * 60 * 1000));
      case 'year': return new Date(now.getFullYear(), 0, 1);
      default: return new Date(now.getTime() - (60 * 24 * 60 * 60 * 1000));
    }
  };

  // Función para obtener entidades filtradas por período para el modal (producciones y muestras con períodos independientes)
  const getFilteredEntitiesForModal = useMemo(() => {
    const startDateProd = getStartDateForPeriod(compliancePeriod);
    const startDateSamples = getStartDateForPeriod(compliancePeriodSamples);
    
      let allEntities: any[] = [];
      
      // Agregar producciones con su tipo (período de producciones)
      const filteredProductions = productions
        .filter(entity => {
          if (!entity.delivery_date) return false;
          const deliveryDate = new Date(entity.delivery_date);
          return deliveryDate >= startDateProd;
        })
        .map(entity => ({ ...entity, type: 'production' }));
      
      // Agregar muestras con su tipo (período de muestras)
      const filteredSamples = samples
        .filter(entity => {
          if (!entity.delivery_date) return false;
          const deliveryDate = new Date(entity.delivery_date);
          return deliveryDate >= startDateSamples;
        })
        .map(entity => ({ ...entity, type: 'sample' }));
      
      // Combinar según el filtro de tipo
      if (entityTypeFilterModal === 'all') {
        allEntities = [...filteredProductions, ...filteredSamples];
      } else if (entityTypeFilterModal === 'production') {
        allEntities = filteredProductions;
      } else if (entityTypeFilterModal === 'sample') {
        allEntities = filteredSamples;
      } else {
        allEntities = [...filteredProductions, ...filteredSamples];
      }
    
      // Agregar información de cumplimiento a cada entidad
      return allEntities.map(entity => {
        const deliveryDate = new Date(entity.delivery_date);
        
        // Solo mostrar fecha de terminación si la entidad está actualmente terminada
        const isFinished = entity.status === 'finished' || entity.status === 'shipped';
        const finishedAt = (isFinished && entity.finished_at) ? new Date(entity.finished_at) : null;
        
        const isOnTime = finishedAt ? finishedAt <= deliveryDate : false;
        const daysDifference = finishedAt ? Math.ceil((finishedAt.getTime() - deliveryDate.getTime()) / (1000 * 60 * 60 * 24)) : null;
      
      return {
        ...entity,
        deliveryDate,
        finishedAt,
        isFinished,
        isOnTime,
        daysDifference,
        clientName: getClientName(entity.client_id)
      };
    }).sort((a, b) => {
      // Ordenar por fecha de entrega según el estado de orden
      return isAscendingOrder 
        ? a.deliveryDate.getTime() - b.deliveryDate.getTime() // Más antiguo primero
        : b.deliveryDate.getTime() - a.deliveryDate.getTime(); // Más nuevo primero
    });
  }, [productions, samples, compliancePeriod, compliancePeriodSamples, clients, isAscendingOrder, entityTypeFilterModal]);

  // Entidades filtradas para el modal según tipo abierto (solo prod o solo muestras)
  const getModalEntitiesByType = useMemo(() => {
    const isProd = deliveryComplianceModalType === 'production';
    const startDate = getStartDateForPeriod(isProd ? compliancePeriod : compliancePeriodSamples);
    const entities = isProd ? productions : samples;
    const type = isProd ? 'production' : 'sample';
    return entities
      .filter((entity: any) => {
        if (!entity.delivery_date) return false;
        const deliveryDate = new Date(entity.delivery_date);
        return deliveryDate >= startDate;
      })
      .map((entity: any) => ({ ...entity, type }))
      .map((entity: any) => {
        const deliveryDate = new Date(entity.delivery_date);
        const isFinished = entity.status === 'finished' || entity.status === 'shipped' || entity.status === 'shipped/despatched';
        const finishedAt = (isFinished && entity.finished_at) ? new Date(entity.finished_at) : null;
        const isOnTime = finishedAt ? finishedAt <= deliveryDate : false;
        const daysDifference = finishedAt ? Math.ceil((finishedAt.getTime() - deliveryDate.getTime()) / (1000 * 60 * 60 * 24)) : null;
        return {
          ...entity,
          deliveryDate,
          finishedAt,
          isFinished,
          isOnTime,
          daysDifference,
          clientName: getClientName(entity.client_id)
        };
      })
      .sort((a: any, b: any) => isAscendingOrder
        ? a.deliveryDate.getTime() - b.deliveryDate.getTime()
        : b.deliveryDate.getTime() - a.deliveryDate.getTime());
  }, [productions, samples, compliancePeriod, compliancePeriodSamples, deliveryComplianceModalType, clients, isAscendingOrder]);

  // Calcular fechas de inicio y fin según el filtro de período
  const now = new Date();
  let periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
  let periodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  if (periodFilter === '7days') {
    periodStart = new Date(now);
    periodStart.setDate(now.getDate() - 7);
    periodEnd = now;
  } else if (periodFilter === '30days') {
    periodStart = new Date(now);
    periodStart.setDate(now.getDate() - 30);
    periodEnd = now;
  } else if (periodFilter === 'quarter') {
    periodStart = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1);
    periodEnd = new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3 + 3, 0);
  } else if (periodFilter === 'year') {
    periodStart = new Date(now.getFullYear(), 0, 1);
    periodEnd = new Date(now.getFullYear(), 11, 31);
  }

  // Calcular métricas de cumplimiento (producciones y muestras con períodos independientes)
  const complianceMetrics = useMemo(() => {
    const now = new Date();
    const calcFor = (entities: Array<{ delivery_date?: string; status: string; finished_at?: string }>, period: string) => {
      const startDate = getStartDateForPeriod(period);
      const filtered = entities.filter(e => e.delivery_date && new Date(e.delivery_date) >= startDate);
      const finished = filtered.filter(e => e.status === 'finished' || e.status === 'shipped' || e.status === 'shipped/despatched');
      const onTime = finished.filter(e => e.delivery_date && e.finished_at && new Date(e.finished_at) <= new Date(e.delivery_date));
      const pct = finished.length > 0 ? Math.round((onTime.length / finished.length) * 100) : 0;
      return { percentage: pct, onTime: onTime.length, finished: finished.length, total: filtered.length, finishedList: finished };
    };
    const productionCompliance = calcFor(productions, compliancePeriod);
    const sampleCompliance = calcFor(samples, compliancePeriodSamples);

    // Promedio de días: combinar producciones (su período) + muestras (su período)
    const allFinished = [...productionCompliance.finishedList, ...sampleCompliance.finishedList];
    const deliveryDaysDiff = allFinished
      .filter(e => e.finished_at && e.delivery_date)
      .map(e => {
        const d = new Date(e.delivery_date!);
        const f = new Date(e.finished_at!);
        return Math.round((f.getTime() - d.getTime()) / (1000 * 60 * 60 * 24));
      });
    const averageDeliveryDaysDiff = deliveryDaysDiff.length > 0
      ? Math.round((deliveryDaysDiff.reduce((s, d) => s + d, 0) / deliveryDaysDiff.length) * 10) / 10
      : 0;

    // Para el modal: totales combinados
    const totalEntities = productionCompliance.total + sampleCompliance.total;
    const finishedEntities = productionCompliance.finished + sampleCompliance.finished;
    const onTimeFinishedEntities = productionCompliance.onTime + sampleCompliance.onTime;
    const deliveryCompliance = finishedEntities > 0 ? Math.round((onTimeFinishedEntities / finishedEntities) * 100) : 0;

    return {
      productionCompliance,
      sampleCompliance,
      totalEntities,
      finishedEntities,
      onTimeFinishedEntities,
      deliveryCompliance,
      averageDeliveryDaysDiff,
    };
  }, [productions, samples, compliancePeriod, compliancePeriodSamples]);

  // Filtrar tareas por proveedor, estado y período
  const filteredSupplierTasks = [...productionTasks, ...sampleTasks].filter(task => {
    const taskStart = task.start_date ? new Date(task.start_date) : null;
    const taskEnd = task.due_date ? new Date(task.due_date) : null;
    const inPeriod = (!taskStart || (taskStart >= periodStart && taskStart <= periodEnd)) ||
                    (!taskEnd || (taskEnd >= periodStart && taskEnd <= periodEnd));
    const supplierOk = !supplierFilter || task.supplier_id === supplierFilter;
    const statusOk = !statusFilter || task.status === statusFilter;
    
    // Filtrar por tipo de entidad
    const isProductionTask = !!task.production_id;
    const isSampleTask = !!task.sample_id;
    const entityTypeOk = entityTypeFilter === 'all' || 
                        (entityTypeFilter === 'production' && isProductionTask) ||
                        (entityTypeFilter === 'sample' && isSampleTask);
    
    // Filtrar tareas de producciones/muestras terminadas
    const production = productions.find(p => p.id === task.production_id);
    const sample = samples.find(s => s.id === task.sample_id);
    const entity = production || sample;
    
    // Si no se encuentra la entidad, excluir la tarea
    if (!entity) {
      return false;
    }
    
    // Verificar que la entidad no esté terminada
    const entityNotFinished = production 
      ? production.status !== 'finished'
      : sample.status !== 'finished' && sample.status !== 'shipped';
    
    return inPeriod && supplierOk && statusOk && entityTypeOk && entityNotFinished;
  });
  
  // Get the selected entity
  const selectedEntity = useMemo<Production | Sample | null>(() => {
    if (!selectedEntityId) return null;
    
    if (entityType === 'production') {
      return productions.find(p => p.id === selectedEntityId) || null;
    } else {
      return samples.find(s => s.id === selectedEntityId) || null;
    }
  }, [selectedEntityId, entityType, productions, samples]);
  
  // Get tasks for the selected entity
  const entityTasks = useMemo(() => {
    if (!selectedEntityId) return [];
    
    if (entityType === 'production') {
      return productionTasks.filter(task => task.production_id === selectedEntityId);
    } else {
      return sampleTasks.filter(task => task.sample_id === selectedEntityId);
    }
  }, [selectedEntityId, entityType, productionTasks, sampleTasks]);
  
  // Filter out completed/shipped entities
  const activeEntities = useMemo(() => {
    if (entityType === 'production') {  
      return productions.filter(p => p.status !== 'finished');
    } else {
      return samples.filter(s => s.status !== 'finished' && s.status !== 'shipped');
    }
  }, [entityType, productions, samples]);
  
  // Get all active productions and samples for calendar view
  const calendarEntities = useMemo(() => {
    const activeProductions = productions.filter(p => p.status !== 'finished');
    const activeSamples = samples.filter(s => s.status !== 'finished' && s.status !== 'shipped');
    
    // Función para ajustar fechas de entrega a días laborables
    const adjustToWorkday = (dateString: string) => {
      const date = new Date(dateString);
      const dayOfWeek = date.getDay();
      
      // Si es sábado (6) o domingo (0), mover al viernes anterior
      if (dayOfWeek === 6) { // Sábado
        date.setDate(date.getDate() - 1);
      } else if (dayOfWeek === 0) { // Domingo
        date.setDate(date.getDate() - 2);
      }
      
      return date.toISOString().split('T')[0];
    };
    
    return [
      ...activeProductions.map(p => ({
        id: p.id,
        type: 'production' as const,
        name: p.name,
        client_id: p.client_id,
        delivery_date: adjustToWorkday(p.delivery_date),
        status: p.status,
        original_date: p.delivery_date // Guardar fecha original para mostrar
      })),
      ...activeSamples.map(s => ({
        id: s.id,
        type: 'sample' as const,
        name: s.name,
        client_id: s.client_id,
        delivery_date: adjustToWorkday(s.delivery_date),
        status: s.status,
        original_date: s.delivery_date // Guardar fecha original para mostrar
      }))
    ];
  }, [productions, samples]);
  
  // Get supplier name for a task
  const getSupplierName = (supplierId: string | null) => {
    if (!supplierId) return 'Sin proveedor';
    const supplier = suppliers.find(s => s.id === supplierId);
    return supplier ? supplier.name : 'Proveedor no encontrado';
  };
  
  // Handle task status change (for Kanban drag and drop)
  const handleTaskStatusChange = async (taskId: string, newStatus: 'pending' | 'in_progress' | 'finished') => {
    try {
      if (entityType === 'production') {
        const task = productionTasks.find(t => t.id === taskId);
        if (task) {
          await updateProductionTask(taskId, { status: newStatus });
        }
      } else {
        const task = sampleTasks.find(t => t.id === taskId);
        if (task) {
          await updateSampleTask(taskId, { status: newStatus });
        }
      }
    } catch (error: any) {
      console.error('Error updating task status:', error);
      setError(error.message || 'Error al actualizar el estado de la tarea');
      setShowErrorModal(true);
    }
  };

  // Handle drag and drop for calendar entities
  const handleDragStart = (e: React.DragEvent, entity: {id: string, type: EntityType}) => {
    console.log('🎯 DRAG START:', entity.name, entity.id, entity.type);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', JSON.stringify(entity));
    setDraggedEntity(entity);
    setIsDragging(true);
  };

  const handleDragEnd = () => {
    setDraggedEntity(null);
    setIsDragging(false);
    setDragOverDay(null);
  };

  const handleDragOver = (e: React.DragEvent, day: Date) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverDay(day);
  };

  const handleDrop = async (e: React.DragEvent, targetDate: Date) => {
    e.preventDefault();
    e.stopPropagation();
    
    console.log('🎯 DROP on day:', format(targetDate, 'dd/MM/yyyy'));
    
    // Intentar obtener la entidad arrastrada del dataTransfer si no está en el estado
    let entityToUpdate = draggedEntity;
    if (!entityToUpdate) {
      try {
        const data = e.dataTransfer.getData('text/plain');
        if (data) {
          entityToUpdate = JSON.parse(data);
          console.log('Recovered entity from dataTransfer:', entityToUpdate);
        }
      } catch (error) {
        console.error('Error parsing drag data:', error);
      }
    }
    
    if (!entityToUpdate) {
      console.log('No dragged entity found');
      return;
    }

    setIsUpdatingDate(true);

    try {
      const newDeliveryDate = targetDate.toISOString().split('T')[0];
      
      if (entityToUpdate.type === 'production') {
        await updateProduction(entityToUpdate.id, { delivery_date: newDeliveryDate });
      } else {
        await updateSample(entityToUpdate.id, { delivery_date: newDeliveryDate });
      }
      
      // Mostrar notificación de éxito
      const entity = entityToUpdate.type === 'production' 
        ? productions.find(p => p.id === entityToUpdate.id)
        : samples.find(s => s.id === entityToUpdate.id);
      
      if (entity) {
        // Usar el toast del sistema si está disponible
        if (window.toast) {
          window.toast.success(`Fecha de entrega actualizada para ${entity.name} al ${format(targetDate, 'dd/MM/yyyy')}`);
        }
      }
      
      setDraggedEntity(null);
      setIsDragging(false);
      setDragOverDay(null);
    } catch (error: any) {
      console.error('Error updating delivery date:', error);
      setError(error.message || 'Error al actualizar la fecha de entrega');
      setShowErrorModal(true);
    } finally {
      setIsUpdatingDate(false);
    }
  };
  
  // Calculate total cost of tasks for selected entity
  const totalTasksCost = useMemo(() => {
    return entityTasks.reduce((sum, task) => sum + task.cost, 0);
  }, [entityTasks]);
  
  // Calculate task status counts
  const taskStatusCounts = useMemo(() => {
    return {
      pending: entityTasks.filter(task => task.status === 'pending').length,
      inProgress: entityTasks.filter(task => task.status === 'in_progress').length,
      finished: entityTasks.filter(task => task.status === 'finished').length,
    };
  }, [entityTasks]);
  
  // Filter entities based on search query
  const filteredEntities = useMemo(() => {
    let entities = activeEntities;

    if (searchQuery) {
      entities = entities.filter(entity => {
        const clientName = getClientName(entity.client_id);
        return (
          entity.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          clientName.toLowerCase().includes(searchQuery.toLowerCase())
        );
      });
    }

    // Sort by delivery_date (closest first)
    return entities.sort((a, b) => {
      if (!a.delivery_date && !b.delivery_date) return 0;
      if (!a.delivery_date) return 1;
      if (!b.delivery_date) return -1;
      return new Date(a.delivery_date).getTime() - new Date(b.delivery_date).getTime();
    });
  }, [activeEntities, searchQuery]);
  
  // Handle entity type change
  const handleEntityTypeChange = (type: EntityType) => {
    setEntityType(type);
    setSelectedEntityId(null);
    setSearchQuery('');
  };
  
  // Handle entity selection
  const handleEntitySelect = (id: string) => {
    const previousEntityId = selectedEntityId;
    setSelectedEntityId(id);
    
    // Si no había nada seleccionado antes y ahora se selecciona algo, cambiar a vista de tareas
    if (!previousEntityId && id) {
      setViewMode('timeline');
    }
  };
  
  // Get file links for the selected entity
  const getFileLinks = () => {
    if (!selectedEntity) return [];
    
    const files = [];
    
    // Debug: Log the selected entity to see what fields are available
    console.log('🔍 Selected Entity:', selectedEntity);
    console.log('🔍 Entity Type:', entityType);
    console.log('🔍 Approved File 2:', (selectedEntity as any).approved_file_2);
    
    // Primer archivo de ficha aprobada
    if (selectedEntity.approved_file) {
      files.push({
        name: 'Ficha Aprobada',
        url: selectedEntity.approved_file
      });
    }
    
    // Segundo archivo de ficha aprobada (si existe)
    if (entityType === 'production' && (selectedEntity as Production).approved_file_2) {
      files.push({
        name: 'Ficha Aprobada (2)',
        url: (selectedEntity as Production).approved_file_2
      });
    } else if (entityType === 'sample' && (selectedEntity as Sample).approved_file_2) {
      files.push({
        name: 'Ficha Aprobada (2)',
        url: (selectedEntity as Sample).approved_file_2
      });
    }
    
    if (selectedEntity.dtf_file) {
      files.push({
        name: 'Archivo DTF',
        url: selectedEntity.dtf_file
      });
    }
    
    if (entityType === 'production' && (selectedEntity as Production).embroidery_file) {
      files.push({
        name: 'Archivo Bordado',
        url: (selectedEntity as Production).embroidery_file
      });
    }
    
    if (entityType === 'sample' && (selectedEntity as Sample).shipping_label) {
      files.push({
        name: 'Etiqueta de Envío',
        url: (selectedEntity as Sample).shipping_label
      });
    }
    
    console.log('🔍 Files found:', files);
    return files;
  };
  
  // Handle adding a new task
  const handleAddTask = () => {
    setSelectedTask(null);
    setIsTaskModalOpen(true);
  };
  
  // Handle editing a task
  const handleEditTask = (task: ProductionTask | SampleTask) => {
    setSelectedTask(task);
    setIsTaskModalOpen(true);
  };
  
  // Handle deleting a task
  const handleDeleteTask = (id: string) => {
    setTaskToDelete(id);
    setIsDeleteModalOpen(true);
  };
  
  // Confirm task deletion
  const confirmDeleteTask = async () => {
    if (!taskToDelete) return;
    
    try {
      if (entityType === 'production') {
        await deleteProductionTask(taskToDelete);
      } else {
        await deleteSampleTask(taskToDelete);
      }
    } catch (error: any) {
      console.error('Error deleting task:', error);
      setError(error.message || 'Error al eliminar la tarea');
      setShowErrorModal(true);
    }
  };
  
  // Save a task
  const handleSaveTask = async (task: Omit<ProductionTask | SampleTask, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      if (entityType === 'production') {
        if (selectedTask) {
          await updateProductionTask(selectedTask.id, task as Omit<ProductionTask, 'id' | 'created_at' | 'updated_at'>);
        } else {
          await addProductionTask(task as Omit<ProductionTask, 'id' | 'created_at' | 'updated_at'>);
        }
      } else {
        if (selectedTask) {
          await updateSampleTask(selectedTask.id, task as Omit<SampleTask, 'id' | 'created_at' | 'updated_at'>);
        } else {
          await addSampleTask(task as Omit<SampleTask, 'id' | 'created_at' | 'updated_at'>);
        }
      }
      setIsTaskModalOpen(false);
    } catch (error: any) {
      console.error('Error saving task:', error);
      setError(error.message || 'Error al guardar la tarea');
      setShowErrorModal(true);
    }
  };

  // Update task dates from Gantt view
  const handleUpdateTaskDates = async (taskId: string, startDate: string, endDate: string) => {
    try {
      const task = [...productionTasks, ...sampleTasks].find(t => t.id === taskId);
      if (!task) return;

      if (entityType === 'production') {
        await updateProductionTask(taskId, {
          ...task,
          start_date: startDate,
          end_date: endDate,
        } as Omit<ProductionTask, 'id' | 'created_at' | 'updated_at'>);
      } else {
        await updateSampleTask(taskId, {
          ...task,
          start_date: startDate,
          end_date: endDate,
        } as Omit<SampleTask, 'id' | 'created_at' | 'updated_at'>);
      }
    } catch (error: any) {
      console.error('Error updating task dates:', error);
      setError(error.message || 'Error al actualizar las fechas de la tarea');
      setShowErrorModal(true);
    }
  };

  // Función para abrir modal de fecha de terminación
  const handleMarkAsFinished = (entityId: string, entityType: EntityType, entityName: string) => {
    setEntityToFinish({ id: entityId, type: entityType, name: entityName });
    setFinishDate(new Date().toISOString().split('T')[0]); // Fecha de hoy por defecto
    setIsFinishDateModalOpen(true);
  };

    // Función para confirmar terminación con fecha seleccionada
    const handleConfirmFinish = async () => {
      if (!entityToFinish) return;
      
      try {
        setIsMarkingAsFinished(true);
        
        // Agregar un día a la fecha seleccionada
        const selectedDate = new Date(finishDate);
        selectedDate.setDate(selectedDate.getDate() + 1);
        const finishedAt = selectedDate.toISOString();
        
        if (entityToFinish.type === 'production') {
          await updateProduction(entityToFinish.id, { status: 'finished', finished_at: finishedAt });
        } else {
          await updateSample(entityToFinish.id, { status: 'finished', finished_at: finishedAt });
        }
        
        setIsFinishDateModalOpen(false);
        
        // Crear encuesta de satisfacción solo para producciones (no para muestras)
        if (entityToFinish.type === 'production') {
          const entity = productions.find(p => p.id === entityToFinish.id) as any;
          if (entity?.salesperson) {
            const client = clients.find(c => c.id === entity.client_id);
            const result = await createSatisfactionSurvey({
              entityType: 'production',
              entityId: entityToFinish.id,
              entityName: entityToFinish.name,
              salesperson: entity.salesperson,
              clientId: entity.client_id,
              clientName: getClientName(entity.client_id),
              clientEmail: (client as any)?.email || undefined,
            });
            if (result) {
              setSurveyLinkModal({ token: result.token, entityName: entityToFinish.name });
            }
          }
        }
        
        setEntityToFinish(null);
        setFinishDate('');
      } catch (error: any) {
        console.error('Error marking as finished:', error);
        setError(error.message || 'Error al marcar como terminado');
        setShowErrorModal(true);
      } finally {
        setIsMarkingAsFinished(false);
      }
    };

    // Función para iniciar edición de fecha
    const handleStartEdit = (entityId: string, entityType: EntityType, field: 'delivery' | 'finish', currentValue: string) => {
      setEditingEntity({ id: entityId, type: entityType, field });
      setEditingValue(currentValue);
    };

    // Función para cancelar edición
    const handleCancelEdit = () => {
      setEditingEntity(null);
      setEditingValue('');
    };

    // Función para guardar edición de fecha
    const handleSaveEdit = async () => {
      if (!editingEntity) return;
      
      try {
        const updateData: any = {};
        
        if (editingEntity.field === 'delivery') {
          updateData.delivery_date = editingValue;
        } else if (editingEntity.field === 'finish') {
          if (editingValue) {
            // Agregar un día a la fecha de terminación
            const selectedDate = new Date(editingValue);
            selectedDate.setDate(selectedDate.getDate() + 1);
            updateData.finished_at = selectedDate.toISOString();
          } else {
            updateData.finished_at = null;
          }
        }
        
        if (editingEntity.type === 'production') {
          await updateProduction(editingEntity.id, updateData);
        } else {
          await updateSample(editingEntity.id, updateData);
        }
        
        setEditingEntity(null);
        setEditingValue('');
      } catch (error: any) {
        console.error('Error updating date:', error);
        setError(error.message || 'Error al actualizar la fecha');
        setShowErrorModal(true);
      }
    };
  
  // Format entity options for dropdown
  const entityOptions = useMemo(() => {
    const now = new Date();
    const FOUR_DAYS_MS = 4 * 24 * 60 * 60 * 1000;
    return [
      { value: '', label: `Seleccionar ${entityType === 'production' ? 'producción' : 'muestra'}...`, searchText: '' },
      ...filteredEntities.map(entity => {
        const clientName = getClientName(entity.client_id);
        const delivery = entity.delivery_date ? new Date(entity.delivery_date) : null;
        const daysLeft = delivery ? Math.ceil((delivery.getTime() - now.getTime()) / (1000*60*60*24)) : null;
        const dd = delivery ? delivery.getDate().toString() : '';
        const mm = delivery ? (delivery.getMonth()+1).toString() : '';
        const dateShort = delivery ? `${dd}/${mm}` : '';
        const urgent = delivery && daysLeft !== null && daysLeft <= 7;
        const dateNode = delivery ? (
          <span className={`font-bold ${urgent ? 'text-red-600' : 'text-black'}`}>{dateShort}</span>
        ) : null;
        const createdAt = entity.created_at ? new Date(entity.created_at) : null;
        const isNew =
          createdAt !== null &&
          !Number.isNaN(createdAt.getTime()) &&
          now.getTime() - createdAt.getTime() <= FOUR_DAYS_MS;
        const newPill = isNew ? (
          <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300">
            Nueva
          </span>
        ) : null;
        return {
          value: entity.id,
          label: (
            <span className="inline-flex items-center flex-wrap gap-x-1">
              {entity.name} - {clientName} {dateNode && <>{' '}{dateNode}</>}
              {newPill}
            </span>
          ),
          searchText: `${entity.name} - ${clientName} ${dateShort}${isNew ? ' nueva' : ''}`
        };
      })
    ];
  }, [filteredEntities, entityType]);
  
  // Calendar view functions
  const getCalendarDays = () => {
    const today = new Date();
    const startDate = startOfWeek(today, { weekStartsOn: 1 }); // Start from Monday
    const endDate = endOfWeek(addWeeks(startDate, 3), { weekStartsOn: 1 }); // 4 weeks total
    
    // Solo incluir días laborables (lunes a viernes)
    const allDays = eachDayOfInterval({ start: startDate, end: endDate });
    return allDays.filter(day => day.getDay() !== 0 && day.getDay() !== 6); // Excluir domingo (0) y sábado (6)
  };
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200'; 
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-200';
    }
  };
  
  // Get type color based on entity type (production or sample)
  const getTypeColor = (type: 'production' | 'sample') => {
    const isDark = document.documentElement.classList.contains('dark');
    
    if (type === 'production') {
      return isDark 
        ? { backgroundColor: 'rgba(120 53 15, 0.3)', color: 'rgb(253 230 138)' } // amber-900/30 text-amber-200
        : { backgroundColor: 'rgb(254 243 199)', color: 'rgb(146 64 14)' }; // amber-100 text-amber-800
    } else {
      return isDark 
        ? { backgroundColor: 'rgba(30 58 138, 0.3)', color: 'rgb(191 219 254)' } // blue-900/30 text-blue-200
        : { backgroundColor: 'rgb(219 234 254)', color: 'rgb(30 64 175)' }; // blue-100 text-blue-800
    }
  };
  
  // Get type icon based on entity type
  const getTypeIcon = (type: 'production' | 'sample') => {
    return type === 'production' 
      ? <Package className="h-3 w-3 mr-1" />
      : <Scissors className="h-3 w-3 mr-1" />;
  };
  
  const calendarDays = useMemo(() => getCalendarDays(), []);
  
  // Render Calendar View
  const renderCalendarView = () => {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header compacto */}
        <div className="bg-gray-50 dark:bg-gray-700 px-3 py-2 border-b border-gray-200 dark:border-gray-600">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">Calendario de Entregas</h3>
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              {isDragging && (
                <span className="flex items-center gap-1 text-blue-600 dark:text-blue-400">
                  <Clock className="h-3 w-3" />
                  Arrastra para mover
                </span>
              )}
              {isUpdatingDate && (
                <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                  <Clock className="h-3 w-3 animate-spin" />
                  Actualizando...
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Calendario compacto */}
        <div className="p-2">
          <div className="grid grid-cols-5 gap-1">
            {/* Headers de días - más compactos */}
            {['L', 'M', 'X', 'J', 'V'].map((day, index) => (
              <div key={index} className="text-center text-xs font-semibold text-gray-600 dark:text-gray-400 py-2 bg-gray-100 dark:bg-gray-700 rounded">
                {day}
              </div>
            ))}
            
            {/* Celdas del calendario - más compactas */}
            {calendarDays.map((day, index) => {
              const isToday = isSameDay(day, new Date());
              const dayEntities = calendarEntities.filter(entity => 
                isSameDay(parseISO(entity.delivery_date), day)
              );
              
              const weekendEntities = calendarEntities.filter(entity => {
                const originalDate = new Date(entity.original_date);
                const dayOfWeek = originalDate.getDay();
                return (dayOfWeek === 6 || dayOfWeek === 0) && isSameDay(parseISO(entity.delivery_date), day);
              });
              
              return (
                <div 
                  key={index} 
                  className={`min-h-[120px] border border-gray-200 dark:border-gray-600 rounded-lg p-2 transition-all duration-200 ${
                    isToday 
                      ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-600' 
                      : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700'
                  } ${
                    isDragging ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-400 dark:border-blue-500' : ''
                  } ${
                    dragOverDay && isSameDay(dragOverDay, day) 
                      ? 'bg-green-50 dark:bg-green-900/20 border-green-400 dark:border-green-500 ring-2 ring-green-200 dark:ring-green-800' 
                      : ''
                  }`}
                  onDragOver={(e) => handleDragOver(e, day)}
                  onDrop={(e) => handleDrop(e, day)}
                >
                  {/* Header de la celda - compacto */}
                  <div className="flex items-center justify-between mb-2">
                    <div className={`text-sm font-bold ${
                      isToday ? 'text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'
                    }`}>
                      {format(day, 'dd')}
                    </div>
                    {dayEntities.length > 0 && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded-full">
                        {dayEntities.length}
                      </div>
                    )}
                  </div>
                  
                  {/* Lista de entregas - compacta */}
                  <div className="space-y-1 max-h-[85px] overflow-y-auto">
                    {dayEntities.length === 0 ? (
                      <div className="text-center py-2 text-gray-400 dark:text-gray-500 text-xs">
                        —
                      </div>
                    ) : (
                      dayEntities.map(entity => {
                        const isWeekendEntity = weekendEntities.some(we => we.id === entity.id);
                        return (
                          <div 
                            key={entity.id} 
                            className={`text-xs p-2 rounded border cursor-grab transition-all duration-200 ${
                              draggedEntity?.id === entity.id ? 'opacity-50 scale-95' : ''
                            } ${
                              isWeekendEntity ? 'border-orange-300 bg-orange-50 dark:bg-orange-900/20' : 'border-gray-200 dark:border-gray-600'
                            } ${
                              entity.type === 'production' 
                                ? 'bg-amber-50 text-amber-800 dark:bg-amber-900/20 dark:text-amber-200' 
                                : 'bg-blue-50 text-blue-800 dark:bg-blue-900/20 dark:text-blue-200'
                            } hover:shadow-sm`}
                            draggable={true}
                            onDragStart={(e) => handleDragStart(e, {id: entity.id, type: entity.type})}
                            onDragEnd={handleDragEnd}
                            onDoubleClick={() => {
                              setEntityType(entity.type);
                              setSelectedEntityId(entity.id);
                              setViewMode('timeline');
                            }}
                            title={`${isWeekendEntity ? '⚠️ Fecha original: ' + format(new Date(entity.original_date), 'dd/MM/yyyy') + ' (fin de semana) - ' : ''}Arrastra para cambiar fecha`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-1 min-w-0 flex-1">
                                {getTypeIcon(entity.type)}
                                <span className="truncate font-medium text-xs">{entity.name}</span>
                              </div>
                              <div className="flex items-center gap-1">
                                {isWeekendEntity && (
                                  <span className="text-orange-600 dark:text-orange-400 text-xs">⚠️</span>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMarkAsFinished(entity.id, entity.type, entity.name);
                                  }}
                                  disabled={isMarkingAsFinished}
                                  className="text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 text-xs bg-emerald-100 dark:bg-emerald-900/30 px-1.5 py-0.5 rounded hover:bg-emerald-200 dark:hover:bg-emerald-900/50 transition-colors"
                                  title="Marcar como terminado"
                                >
                                  ✓
                                </button>
                              </div>
                            </div>
                            <div className="text-xs opacity-70 truncate mt-0.5">
                              {getClientName(entity.client_id)}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                  {/* Indicadores de estado */}
                  {isDragging && (
                    <div className="mt-1 text-xs text-blue-600 dark:text-blue-400 text-center">
                      {dragOverDay && isSameDay(dragOverDay, day) ? 'Suelta aquí' : 'Arrastra aquí'}
                    </div>
                  )}
                  {dragOverDay && isSameDay(dragOverDay, day) && (
                    <div className="mt-1 text-xs text-green-600 dark:text-green-400 text-center">
                      ✓ Fecha válida
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };
  
  return (
    <div className="space-y-3">
<div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
  <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white">
    Gestión de {entityType === 'production' ? 'Producción' : 'Muestras'}
  </h2>
  <div className="flex items-center gap-2 w-full sm:w-auto">
    <div className="flex bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-1">
      <button
        onClick={() => handleEntityTypeChange('production')}
        className={`flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
          entityType === 'production'
            ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
        }`}
      >
        <Package className="h-4 w-4 mr-2" />
        Producciones
      </button>
      <button
        onClick={() => handleEntityTypeChange('sample')}
        className={`flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
          entityType === 'sample'
            ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
        }`}
      >
        <Scissors className="h-4 w-4 mr-2" />
        Muestras
      </button>
    </div>
    
    {/* Badges de estado de tareas */}
    {selectedEntityId && (
      <div className="flex items-center gap-2 ml-4">
        <div className="flex items-center gap-1 px-2 py-1 bg-amber-100 dark:bg-amber-900/30 rounded-full">
          <AlertTriangle className="h-3 w-3 text-amber-600 dark:text-amber-400" />
          <span className="text-xs font-medium text-amber-600 dark:text-amber-400">{taskStatusCounts.pending}</span>
        </div>
        <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 rounded-full">
          <Clock className="h-3 w-3 text-blue-600 dark:text-blue-400" />
          <span className="text-xs font-medium text-blue-600 dark:text-blue-400">{taskStatusCounts.inProgress}</span>
        </div>
        <div className="flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 rounded-full">
          <CheckCircle className="h-3 w-3 text-green-600 dark:text-green-400" />
          <span className="text-xs font-medium text-green-600 dark:text-green-400">{taskStatusCounts.finished}</span>
        </div>
      </div>
    )}
    
    <div className="w-full sm:w-[28rem]">
      <Select
        label=""
        value={selectedEntityId || ''}
        onChange={handleEntitySelect}
        options={entityOptions}
        searchable={true}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        className="border border-black h-9"
      />
    </div>
  </div>
</div>

{/* Navigation Tabs - Compact */}
<div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden mb-4">
  <div className="flex items-center justify-between px-3 py-2">
    {/* Left tabs */}
    <div className="flex items-center space-x-1">
      <button
        onClick={() => setViewMode('calendar')}
        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 flex items-center space-x-1.5 ${
          viewMode === 'calendar' 
            ? 'bg-black text-white shadow-sm' 
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
        }`}
      >
        <Calendar className="h-3.5 w-3.5" />
        <span>Calendario</span>
      </button>
      <button
        onClick={() => setViewMode('supplier')}
        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 flex items-center space-x-1.5 ${
          viewMode === 'supplier' 
            ? 'bg-black text-white shadow-sm' 
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
        }`}
      >
        <Package className="h-3.5 w-3.5" />
        <span>Tareas por proveedor</span>
      </button>
    </div>

    {/* Right section - PRODUCCIÓN, Tareas, Kanban, Gantt and Mark as Finished aligned to the right */}
    <div className="flex items-center space-x-1">
      {/* Entity Type Indicator */}
      <div className={`px-2 py-1 text-sm font-bold rounded-md ${
        entityType === 'production' 
          ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200' 
          : 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200'
      }`}>
        {entityType === 'production' ? 'PRODUCCIÓN' : 'MUESTRA'}
      </div>

      {(userRole === 'Producción' || userRole === 'Administrador' || userRole === 'Super Admin') && (
        <button
          onClick={() => setIsSupplierModalOpen(true)}
          className="px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 flex items-center space-x-1.5 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
          title="Crear nuevo proveedor"
        >
          <Plus className="h-3.5 w-3.5" />
          <span>Nuevo proveedor</span>
        </button>
      )}
      
      <button
        onClick={() => setViewMode('timeline')}
        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 flex items-center space-x-1.5 ${
          viewMode === 'timeline' 
            ? 'bg-black text-white shadow-sm' 
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
        }`}
      >
        <Clock className="h-3.5 w-3.5" />
        <span>Tareas</span>
      </button>
      <button
        onClick={() => setViewMode('kanban')}
        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 flex items-center space-x-1.5 ${
          viewMode === 'kanban' 
            ? 'bg-black text-white shadow-sm' 
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
        }`}
      >
        <Columns className="h-3.5 w-3.5" />
        <span>Kanban</span>
      </button>
      <button
        onClick={() => setViewMode('gantt')}
        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 flex items-center space-x-1.5 ${
          viewMode === 'gantt' 
            ? 'bg-black text-white shadow-sm' 
            : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'
        }`}
      >
        <GanttChart className="h-3.5 w-3.5" />
        <span>Gantt</span>
      </button>
      
      {/* Mark as Finished Button */}
      {selectedEntity && (selectedEntity.status !== 'finished' && selectedEntity.status !== 'shipped') && (
        <button
          onClick={() => handleMarkAsFinished(selectedEntityId!, entityType, selectedEntity?.name || '')}
          disabled={isMarkingAsFinished}
          className="ml-3 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white text-sm font-medium rounded-lg transition-all duration-200 flex items-center space-x-2"
        >
          <CheckCircle className="h-4 w-4" />
          <span>{isMarkingAsFinished ? 'Marcando...' : 'Marcar como Terminado'}</span>
        </button>
      )}
    </div>
  </div>
</div>

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <>

          {/* Métricas de Cumplimiento - Solo en vista de calendario */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
            {/* Card Producciones - con selector de período */}
            <div 
              className={`transition-all duration-500 cursor-pointer hover:shadow-lg rounded-lg border-2 p-2 relative ${
                  complianceMetrics.productionCompliance.percentage >= 90 ? 'bg-gradient-to-br from-emerald-50 to-green-100 dark:from-emerald-900/20 dark:to-green-900/30 border-emerald-200 dark:border-emerald-800' :
                  complianceMetrics.productionCompliance.percentage >= 70 ? 'bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/30 border-blue-200 dark:border-blue-800' :
                  complianceMetrics.productionCompliance.percentage >= 50 ? 'bg-gradient-to-br from-amber-50 to-orange-100 dark:from-amber-900/20 dark:to-orange-900/30 border-amber-200 dark:border-amber-800' :
                  'bg-gradient-to-br from-red-50 to-pink-100 dark:from-red-900/20 dark:to-pink-900/30 border-red-200 dark:border-red-800'
                }`}
                onClick={() => { setDeliveryComplianceModalType('production'); setIsDeliveryComplianceModalOpen(true); }}
              >
                <div className="absolute top-1 right-1 z-10" onClick={(e) => e.stopPropagation()}>
                  <div className="w-16">
                    <Select
                      label=""
                      value={compliancePeriod}
                      onChange={setCompliancePeriod}
                      options={[
                        { value: '7days', label: '7d' },
                        { value: '30days', label: '30d' },
                        { value: '60days', label: '60d' },
                        { value: '90days', label: '90d' },
                        { value: '6months', label: '6m' },
                        { value: 'year', label: '1a' }
                      ]}
                      className="text-xs h-6"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-xs font-medium ${
                      complianceMetrics.productionCompliance.percentage >= 90 ? 'text-emerald-700 dark:text-emerald-300' :
                      complianceMetrics.productionCompliance.percentage >= 70 ? 'text-blue-700 dark:text-blue-300' :
                      complianceMetrics.productionCompliance.percentage >= 50 ? 'text-amber-700 dark:text-amber-300' :
                      'text-red-700 dark:text-red-300'
                    }`}>Producciones</p>
                    <p className={`text-xl font-bold ${
                      complianceMetrics.productionCompliance.percentage >= 90 ? 'text-emerald-600 dark:text-emerald-400' :
                      complianceMetrics.productionCompliance.percentage >= 70 ? 'text-blue-600 dark:text-blue-400' :
                      complianceMetrics.productionCompliance.percentage >= 50 ? 'text-amber-600 dark:text-amber-400' :
                      'text-red-600 dark:text-red-400'
                    }`}>{complianceMetrics.productionCompliance.percentage}%</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{complianceMetrics.productionCompliance.onTime} de {complianceMetrics.productionCompliance.finished} a tiempo</p>
                  </div>
                  <CheckCircle className={`h-5 w-5 ${
                    complianceMetrics.productionCompliance.percentage >= 90 ? 'text-emerald-600' :
                    complianceMetrics.productionCompliance.percentage >= 70 ? 'text-blue-600' :
                    complianceMetrics.productionCompliance.percentage >= 50 ? 'text-amber-600' : 'text-red-600'
                  }`} />
                </div>
              </div>
            {/* Card Muestras */}
            <div 
              className={`transition-all duration-500 cursor-pointer hover:shadow-lg rounded-lg border-2 p-2 relative ${
                  complianceMetrics.sampleCompliance.percentage >= 90 ? 'bg-gradient-to-br from-emerald-50 to-green-100 dark:from-emerald-900/20 dark:to-green-900/30 border-emerald-200 dark:border-emerald-800' :
                  complianceMetrics.sampleCompliance.percentage >= 70 ? 'bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/30 border-blue-200 dark:border-blue-800' :
                  complianceMetrics.sampleCompliance.percentage >= 50 ? 'bg-gradient-to-br from-amber-50 to-orange-100 dark:from-amber-900/20 dark:to-orange-900/30 border-amber-200 dark:border-amber-800' :
                  'bg-gradient-to-br from-red-50 to-pink-100 dark:from-red-900/20 dark:to-pink-900/30 border-red-200 dark:border-red-800'
                }`}
                onClick={() => { setDeliveryComplianceModalType('sample'); setIsDeliveryComplianceModalOpen(true); }}
              >
                <div className="absolute top-1 right-1 z-10" onClick={(e) => e.stopPropagation()}>
                  <div className="w-16">
                    <Select
                      label=""
                      value={compliancePeriodSamples}
                      onChange={setCompliancePeriodSamples}
                      options={[
                        { value: '7days', label: '7d' },
                        { value: '30days', label: '30d' },
                        { value: '60days', label: '60d' },
                        { value: '90days', label: '90d' },
                        { value: '6months', label: '6m' },
                        { value: 'year', label: '1a' }
                      ]}
                      className="text-xs h-6"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-xs font-medium ${
                      complianceMetrics.sampleCompliance.percentage >= 90 ? 'text-emerald-700 dark:text-emerald-300' :
                      complianceMetrics.sampleCompliance.percentage >= 70 ? 'text-blue-700 dark:text-blue-300' :
                      complianceMetrics.sampleCompliance.percentage >= 50 ? 'text-amber-700 dark:text-amber-300' :
                      'text-red-700 dark:text-red-300'
                    }`}>Muestras</p>
                    <p className={`text-xl font-bold ${
                      complianceMetrics.sampleCompliance.percentage >= 90 ? 'text-emerald-600 dark:text-emerald-400' :
                      complianceMetrics.sampleCompliance.percentage >= 70 ? 'text-blue-600 dark:text-blue-400' :
                      complianceMetrics.sampleCompliance.percentage >= 50 ? 'text-amber-600 dark:text-amber-400' :
                      'text-red-600 dark:text-red-400'
                    }`}>{complianceMetrics.sampleCompliance.percentage}%</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{complianceMetrics.sampleCompliance.onTime} de {complianceMetrics.sampleCompliance.finished} a tiempo</p>
                  </div>
                  <CheckCircle className={`h-5 w-5 ${
                    complianceMetrics.sampleCompliance.percentage >= 90 ? 'text-emerald-600' :
                    complianceMetrics.sampleCompliance.percentage >= 70 ? 'text-blue-600' :
                    complianceMetrics.sampleCompliance.percentage >= 50 ? 'text-amber-600' : 'text-red-600'
                  }`} />
                </div>
              </div>

            {/* Promedio de días de adelantamiento/retraso */}
            <Card>
              <CardContent className="p-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                      Promedio de Días
                    </p>
                    <p className={`text-xl font-bold ${
                      complianceMetrics.averageDeliveryDaysDiff < 0 ? 'text-emerald-600 dark:text-emerald-400' :
                      complianceMetrics.averageDeliveryDaysDiff > 0 ? 'text-red-600 dark:text-red-400' :
                      'text-gray-900 dark:text-white'
                    }`}>
                      {complianceMetrics.averageDeliveryDaysDiff > 0 ? `+${complianceMetrics.averageDeliveryDaysDiff}` : 
                       complianceMetrics.averageDeliveryDaysDiff < 0 ? `${complianceMetrics.averageDeliveryDaysDiff}` : 
                       '0'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {complianceMetrics.averageDeliveryDaysDiff < 0 ? 'días de adelantamiento' :
                       complianceMetrics.averageDeliveryDaysDiff > 0 ? 'días de retraso' :
                       'entregas puntuales'}
                    </p>
                  </div>
                  <div className={`p-2 rounded-lg ${
                    complianceMetrics.averageDeliveryDaysDiff < 0 ? 'bg-emerald-100 dark:bg-emerald-900/30' :
                    complianceMetrics.averageDeliveryDaysDiff > 0 ? 'bg-red-100 dark:bg-red-900/30' :
                    'bg-gray-100 dark:bg-gray-900/30'
                  }`}>
                    <Clock className={`h-5 w-5 ${
                      complianceMetrics.averageDeliveryDaysDiff < 0 ? 'text-emerald-600 dark:text-emerald-400' :
                      complianceMetrics.averageDeliveryDaysDiff > 0 ? 'text-red-600 dark:text-red-400' :
                      'text-gray-600 dark:text-gray-400'
                    }`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {renderCalendarView()}
        </>
      )}
      
      {/* Nueva pestaña: Tareas por Proveedor */}
      {viewMode === 'supplier' && (
        <Card>
          <CardHeader title="Tareas por Proveedor" />
          <CardContent>
            {/* Filtros por proveedor, estado y período */}
            <div className="flex flex-wrap gap-3 mb-4 items-center">
              <div className="w-40">
                <Select
                  label="Proveedor"
                  value={supplierFilter}
                  onChange={setSupplierFilter}
                  options={[
                    { value: '', label: 'Todos' },
                    ...suppliers.map(s => ({ value: s.id, label: s.name }))
                  ]}
                />
              </div>
              <div className="w-40">
                <Select
                  label="Estado"
                  value={statusFilter}
                  onChange={setStatusFilter}
                  options={[
                    { value: '', label: 'Todos' },
                    { value: 'pending', label: 'No comenzado' },
                    { value: 'in_progress', label: 'En Proceso' },
                    { value: 'finished', label: 'Terminado' },
                  ]}
                />
              </div>
              <div className="w-40">
                <Select
                  label="Período"
                  value={periodFilter}
                  onChange={setPeriodFilter}
                  options={[
                    { value: 'month', label: 'Este mes' },
                    { value: '7days', label: '7 días' },
                    { value: '30days', label: '30 días' },
                    { value: 'quarter', label: 'Trimestre' },
                    { value: 'year', label: 'Este año' },
                  ]}
                />
              </div>
              <div className="w-40">
                <Select
                  label="Tipo"
                  value={entityTypeFilter}
                  onChange={setEntityTypeFilter}
                  options={[
                    { value: 'all', label: 'Ver todo' },
                    { value: 'production', label: 'Producciones' },
                    { value: 'sample', label: 'Muestras' },
                  ]}
                />
              </div>
              <Button
                variant={showOnlyLate ? 'primary' : 'outline'}
                className="h-10 px-4"
                onClick={() => setShowOnlyLate(v => !v)}
              >
                <svg className="w-4 h-4 mr-2 text-red-700 dark:text-red-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                {showOnlyLate ? 'Ver todas' : 'Ver solo atrasadas'}
              </Button>
              <Button
                variant="outline"
                className="h-10 px-4"
                onClick={() => {
                  setSupplierFilter('');
                  setStatusFilter('');
                  setPeriodFilter('month');
                  setEntityTypeFilter('all');
                  setShowOnlyLate(false);
                }}
              >
                Limpiar Filtros
              </Button>
            </div>
            {/* Agrupación de tareas por proveedor */}
            <div className="space-y-4">
              {suppliers.map(supplier => {
                let tasks = filteredSupplierTasks.filter(task => task.supplier_id === supplier.id);
                if (showOnlyLate) {
                  tasks = tasks.filter(t => t.end_date && new Date(t.end_date) < now && t.status !== 'finished');
                }
                if (tasks.length === 0) return null;
                // Tareas atrasadas: fecha de fin menor a hoy y no terminadas
                const atrasadas = tasks.filter(t => t.end_date && new Date(t.end_date) < now && t.status !== 'finished').length;
                return (
                  <div key={supplier.id} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Package className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                        <span className="text-lg font-semibold text-gray-900 dark:text-white">{supplier.name}</span>
                      </div>
                      <div className="flex flex-wrap gap-4 mt-2 md:mt-0">
                        <span className="text-sm text-gray-500">Tareas: <span className="font-bold text-gray-900 dark:text-white">{tasks.length}</span></span>
                        <span className="text-sm text-gray-500">Atrasadas: <span className={`font-bold ${atrasadas > 0 ? 'text-red-600' : 'text-green-600'}`}>{atrasadas}</span></span>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-800">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Tarea</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Producción/Muestra</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Estado</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Fecha de inicio</th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Fecha de fin</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                          {tasks.map(task => {
                            const production = productions.find(p => p.id === task.production_id);
                            const sample = samples.find(s => s.id === task.sample_id);
                            const entity = production || sample;
                            const isAtrasada = task.end_date && new Date(task.end_date) < now && task.status !== 'finished';
                            return (
                              <tr key={task.id} className={isAtrasada ? 'bg-red-50 dark:bg-red-900/20' : ''}>
                                <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                                  <span 
                                    className="cursor-pointer hover:underline hover:text-blue-600 dark:hover:text-blue-400"
                                    onClick={() => {
                                      // Determinar si es tarea de producción o muestra
                                      const taskEntityType = task.production_id ? 'production' : 'sample';
                                      const taskEntityId = task.production_id || task.sample_id;
                                      
                                      // Configurar el contexto para el modal
                                      setEntityType(taskEntityType);
                                      setSelectedEntityId(taskEntityId);
                                      setSelectedTask(task);
                                      setIsTaskModalOpen(true);
                                    }}
                                  >
                                    {task.task_name || '-'}
                                  </span>
                                  {isAtrasada && (
                                    <span className="ml-2 inline-flex items-center text-xs font-bold text-red-700 dark:text-red-400 animate-pulse">
                                      <svg className="w-4 h-4 mr-1 text-red-700 dark:text-red-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                      ¡ATRASADA!
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-500 dark:text-gray-400">
                                  <span className="cursor-pointer hover:underline" onClick={() => {
                                    const entityType = task.production_id ? 'production' : 'sample';
                                    setEntityType(entityType);
                                    setSelectedEntityId(entity?.id || null);
                                    setViewMode('timeline');
                                  }}>
                                    {entity?.name || '-'}
                                  </span>
                                </td>
                                <td className="px-4 py-2 text-sm">
                                  <select
                                    className={`px-2 py-1 rounded-full text-xs font-semibold border-0 focus:ring-2 focus:ring-blue-500 ${
                                      task.status === 'pending' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200' :
                                      task.status === 'in_progress' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200' :
                                      'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                                    }`}
                                    value={task.status}
                                    onChange={async (e) => {
                                      const newStatus = e.target.value as 'pending' | 'in_progress' | 'finished';
                                      try {
                                        if (task.production_id) {
                                          await updateProductionTask(task.id, { status: newStatus });
                                        } else {
                                          await updateSampleTask(task.id, { status: newStatus });
                                        }
                                      } catch (error: any) {
                                        console.error('Error updating task status:', error);
                                        setError(error.message || 'Error al actualizar el estado de la tarea');
                                        setShowErrorModal(true);
                                      }
                                    }}
                                  >
                                    <option value="pending">No comenzado</option>
                                    <option value="in_progress">En Proceso</option>
                                    <option value="finished">Terminado</option>
                                  </select>
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                                  <input
                                    type="date"
                                    className="bg-transparent border-0 p-0 text-sm focus:ring-2 focus:ring-blue-500 rounded"
                                    value={task.start_date ? task.start_date.split('T')[0] : ''}
                                    onChange={async (e) => {
                                      const newDate = e.target.value;
                                      try {
                                        if (task.production_id) {
                                          await updateProductionTask(task.id, { start_date: newDate });
                                        } else {
                                          await updateSampleTask(task.id, { start_date: newDate });
                                        }
                                      } catch (error: any) {
                                        console.error('Error updating task start date:', error);
                                        setError(error.message || 'Error al actualizar la fecha de inicio');
                                        setShowErrorModal(true);
                                      }
                                    }}
                                  />
                                </td>
                                <td className="px-4 py-2 text-sm text-gray-900 dark:text-white">
                                  <input
                                    type="date"
                                    className="bg-transparent border-0 p-0 text-sm focus:ring-2 focus:ring-blue-500 rounded"
                                    value={task.end_date ? task.end_date.split('T')[0] : ''}
                                    onChange={async (e) => {
                                      const newDate = e.target.value;
                                      try {
                                        if (task.production_id) {
                                          await updateProductionTask(task.id, { end_date: newDate });
                                        } else {
                                          await updateSampleTask(task.id, { end_date: newDate });
                                        }
                                      } catch (error: any) {
                                        console.error('Error updating task end date:', error);
                                        setError(error.message || 'Error al actualizar la fecha de fin');
                                        setShowErrorModal(true);
                                      }
                                    }}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
              {/* Si no hay tareas para ningún proveedor */}
              {suppliers.every(supplier => filteredSupplierTasks.filter(task => task.supplier_id === supplier.id).length === 0) && (
                <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                  No hay tareas asignadas a proveedores en este período.
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Selected Entity Details */}
      {selectedEntity && viewMode !== 'calendar' && viewMode !== 'supplier' && (
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Detalles de {entityType === 'production' ? 'Producción' : 'Muestra'}
              </h3>
              {selectedEntity && (selectedEntity.status !== 'finished' && selectedEntity.status !== 'shipped') && (
                <Button
                  variant="primary"
                  icon={<CheckCircle className="h-4 w-4" />}
                  onClick={() => handleMarkAsFinished(selectedEntityId!, entityType, selectedEntity?.name || '')}
                  disabled={isMarkingAsFinished}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {isMarkingAsFinished ? 'Marcando...' : 'Marcar como Terminado'}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              {/* Información principal - 2/3 del espacio */}
              <div className="lg:col-span-2 bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">{selectedEntity.name}</h3>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    Math.round((taskStatusCounts.finished / (taskStatusCounts.pending + taskStatusCounts.inProgress + taskStatusCounts.finished || 1)) * 100) >= 75 ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200' :
                    Math.round((taskStatusCounts.finished / (taskStatusCounts.pending + taskStatusCounts.inProgress + taskStatusCounts.finished || 1)) * 100) >= 50 ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200' :
                    Math.round((taskStatusCounts.finished / (taskStatusCounts.pending + taskStatusCounts.inProgress + taskStatusCounts.finished || 1)) * 100) >= 25 ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200' :
                    'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200'
                  }`}>
                    {Math.round((taskStatusCounts.finished / (taskStatusCounts.pending + taskStatusCounts.inProgress + taskStatusCounts.finished || 1)) * 100)}%
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Cliente:</span>
                    <span className="ml-2 text-gray-900 dark:text-white font-medium">{getClientName(selectedEntity.client_id)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500 dark:text-gray-400">Vendedor:</span>
                    <span className="ml-2 text-gray-900 dark:text-white font-medium">{selectedEntity.salesperson}</span>
                  </div>
                  <div className="md:col-span-2">
                    <span className="text-gray-500 dark:text-gray-400">Fecha de entrega:</span>
                    <span className={`ml-2 font-bold ${
                      (() => {
                        const deliveryDate = new Date(selectedEntity.delivery_date);
                        const today = new Date();
                        const diffTime = deliveryDate.getTime() - today.getTime();
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                        
                        if (diffDays < 0 || diffDays <= 4) return 'text-red-600 dark:text-red-400';
                        if (diffDays <= 7) return 'text-amber-600 dark:text-amber-400';
                        return 'text-gray-900 dark:text-white';
                      })()
                    }`}>
                      {format(new Date(selectedEntity.delivery_date), 'dd/MM/yyyy')}
                      {(() => {
                        const deliveryDate = new Date(selectedEntity.delivery_date);
                        const today = new Date();
                        const diffTime = deliveryDate.getTime() - today.getTime();
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                        
                        if (diffDays < 0) return ' (Vencida)';
                        if (diffDays <= 4) return ` (${diffDays} días)`;
                        if (diffDays <= 7) return ` (${diffDays} días)`;
                        return '';
                      })()}
                    </span>
                  </div>
                </div>
                {selectedEntity.comment && (
                  <div className="mt-3 p-2 bg-gray-50 dark:bg-gray-700 rounded-md">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      <span className="font-medium">Comentario:</span> {selectedEntity.comment}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Archivos Adjuntos - 1/3 del espacio */}
              <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-indigo-600 dark:text-indigo-400" />
                  Archivos Adjuntos
                </h3>
                {getFileLinks().length > 0 ? (
                  <div className="space-y-2">
                    {getFileLinks().map((file, index) => (
                      <a 
                        key={index}
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-md transition-colors group"
                      >
                        <FileText className="h-4 w-4 text-indigo-600 dark:text-indigo-400 mr-2" />
                        <span className="text-sm text-gray-700 dark:text-gray-300 flex-1 truncate">{file.name}</span>
                        <Download className="h-4 w-4 text-gray-500 dark:text-gray-400 group-hover:text-indigo-600 dark:group-hover:text-indigo-400" />
                      </a>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No hay archivos adjuntos</p>
                )}
              </div>
            </div>
            
            
            {viewMode !== 'supplier' && (
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Tareas ({entityTasks.length})
                </h3>
                <Button
                  variant="primary"
                  icon={<Plus className="h-4 w-4" />}
                  onClick={handleAddTask}
                >
                  Agregar Tarea
                </Button>
              </div>
            )}
            
            {viewMode === 'timeline' ? (
              <TimelineView
                tasks={entityTasks as ProductionTask[]}
                onEditTask={handleEditTask}
                onDeleteTask={handleDeleteTask}
                getSupplierName={getSupplierName}
              />
            ) : viewMode === 'kanban' ? (
              <KanbanView
                tasks={entityTasks as ProductionTask[]}
                onEditTask={handleEditTask}
                onDeleteTask={handleDeleteTask}
                getSupplierName={getSupplierName}
                onStatusChange={handleTaskStatusChange}
              />
            ) : viewMode === 'gantt' ? (
              <GanttView
                tasks={entityTasks as ProductionTask[]}
                onEditTask={handleEditTask}
                onDeleteTask={handleDeleteTask}
                getSupplierName={getSupplierName}
                onUpdateTaskDates={handleUpdateTaskDates}
              />
            ) : null}
            
            {/* Botón de Historial de Cambios - Para vistas Timeline, Kanban y Gantt */}
            {selectedEntity && (viewMode === 'timeline' || viewMode === 'kanban' || viewMode === 'gantt') && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setIsHistoryModalOpen(true)}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline"
                >
                  Historial de cambios
                </button>
              </div>
            )}
            
            {/* Costo Total de Tareas - Solo para Administradores */}
            {isAdmin && entityTasks.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
                  <span>Costo total de tareas:</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {formatCurrency(totalTasksCost)}
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {!selectedEntity && viewMode !== 'calendar' && viewMode !== 'supplier' && (
        <>
        </>
      )}
      
      {/* Task Modal */}
      {selectedEntityId && (
        entityType === 'production' ? (
          <ProductionTaskModal
            isOpen={isTaskModalOpen}
            onClose={() => {
              setIsTaskModalOpen(false);
              setSelectedTask(null);
            }}
            onSave={handleSaveTask}
            productionId={selectedEntityId}
            initialData={selectedTask as ProductionTask}
          />
        ) : (
          <SampleTaskModal
            isOpen={isTaskModalOpen}
            onClose={() => {
              setIsTaskModalOpen(false);
              setSelectedTask(null);
            }}
            onSave={handleSaveTask}
            sampleId={selectedEntityId}
            initialData={selectedTask as SampleTask}
          />
        )
      )}
      
      {/* Confirm Delete Modal */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setTaskToDelete(null);
        }}
        onConfirm={confirmDeleteTask}
        title="Eliminar Tarea"
        message="¿Estás seguro de que deseas eliminar esta tarea? Esta acción no se puede deshacer."
        confirmText="Eliminar"
        variant="danger"
      />
      
      {/* Error Modal */}
      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={error || 'Ha ocurrido un error'}
      />

      {/* Modal con link de encuesta de satisfacción */}
      {surveyLinkModal && (
        <SatisfactionSurveyLinkModal
          isOpen={!!surveyLinkModal}
          onClose={() => setSurveyLinkModal(null)}
          token={surveyLinkModal.token}
          entityName={surveyLinkModal.entityName}
        />
      )}

      {/* Bulk Import Modal */}
      <BulkTaskImportModal
        isOpen={isBulkImportOpen}
        onClose={() => setIsBulkImportOpen(false)}
        entityType={entityType}
        onImportComplete={() => {
          // Las tareas se recargan automáticamente desde el store
        }}
      />

      {/* Crear proveedor (Producción/Admin) */}
      {isSupplierModalOpen && (
        <EntityModal
          isOpen={isSupplierModalOpen}
          onClose={() => setIsSupplierModalOpen(false)}
          entityType="supplier"
          onSave={async (supplier) => {
            try {
              await addSupplier(supplier as any);
              setIsSupplierModalOpen(false);
            } catch (error: any) {
              setError(error.message || 'Error al crear el proveedor');
              setShowErrorModal(true);
            }
          }}
        />
      )}

    {/* Delivery Compliance Modal - Producciones o Muestras según origen del click */}
    {isDeliveryComplianceModalOpen && (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4" 
        onClick={() => setIsDeliveryComplianceModalOpen(false)}
      >
        <div 
          className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col" 
          onClick={(e) => e.stopPropagation()}
        >
          {(() => {
            const isProd = deliveryComplianceModalType === 'production';
            const metrics = isProd ? complianceMetrics.productionCompliance : complianceMetrics.sampleCompliance;
            const periodText = getPeriodText(isProd ? compliancePeriod : compliancePeriodSamples);
            const title = isProd ? 'Producciones' : 'Muestras';
            const Icon = isProd ? Package : Scissors;
            return (
              <>
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-3">
                    <div className={isProd ? 'p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg' : 'p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg'}>
                      <Icon className={isProd ? 'h-6 w-6 text-amber-600 dark:text-amber-400' : 'h-6 w-6 text-blue-600 dark:text-blue-400'} />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        Cumplimiento de Entrega · {title}
                      </h2>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {periodText}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setIsDeliveryComplianceModalOpen(false)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  </button>
                </div>

                {/* Contenido */}
                <div className="p-6 overflow-y-auto flex-1">
                  {/* Métricas específicas del tipo */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <div className={`rounded-lg p-4 ${metrics.percentage >= 90 ? 'bg-emerald-100 dark:bg-emerald-900/30' : metrics.percentage >= 70 ? 'bg-blue-100 dark:bg-blue-900/30' : metrics.percentage >= 50 ? 'bg-amber-100 dark:bg-amber-900/30' : 'bg-red-100 dark:bg-red-900/30'}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Cumplimiento</p>
                          <p className="text-2xl font-bold text-gray-900 dark:text-white">{metrics.percentage}%</p>
                        </div>
                        <CheckCircle className="h-8 w-8 text-gray-600 dark:text-gray-400" />
                      </div>
                    </div>
                    <div className="bg-green-100 dark:bg-green-900/30 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-green-700 dark:text-green-300">A Tiempo</p>
                          <p className="text-2xl font-bold text-green-600 dark:text-green-400">{metrics.onTime}</p>
                        </div>
                        <Clock className="h-8 w-8 text-green-600 dark:text-green-400" />
                      </div>
                    </div>
                    <div className="bg-amber-100 dark:bg-amber-900/30 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-amber-700 dark:text-amber-300">Terminadas</p>
                          <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{metrics.finished}</p>
                        </div>
                        <Calendar className="h-8 w-8 text-amber-600 dark:text-amber-400" />
                      </div>
                    </div>
                    <div className="bg-gray-100 dark:bg-gray-900/30 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Total</p>
                          <p className="text-2xl font-bold text-gray-600 dark:text-gray-400">{metrics.total}</p>
                        </div>
                        <Filter className="h-8 w-8 text-gray-600 dark:text-gray-400" />
                      </div>
                    </div>
                  </div>

                  {/* Listado detallado */}
                  <div className="mt-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Detalle ({periodText})
                      </h3>
                      <Button
                        variant="outline"
                        onClick={() => setIsAscendingOrder(!isAscendingOrder)}
                        icon={<ArrowUpDown className="h-4 w-4" />}
                        className="text-xs h-8 px-3"
                      >
                        {isAscendingOrder ? 'Más nuevo primero' : 'Más antiguo primero'}
                      </Button>
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-800">
                          <tr>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-32">
                        Nombre
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24">
                        Cliente
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-20">
                        Entrega
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-20">
                        Terminación
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-16">
                        Estado
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-20">
                        Diferencia
                      </th>
                      <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider w-20">
                        Cumplimiento
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {getModalEntitiesByType.map((entity: any) => (
                      <tr key={`${entity.id}-${entity.type}`} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="px-2 py-2 whitespace-nowrap">
                          <div className="text-xs font-medium text-gray-900 dark:text-white truncate max-w-[120px]" title={entity.name}>
                            {entity.name}
                          </div>
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap">
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[80px]" title={entity.clientName}>
                            {entity.clientName}
                          </div>
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap">
                          {editingEntity?.id === entity.id && editingEntity?.field === 'delivery' ? (
                            <div className="flex items-center gap-1">
                              <input
                                type="date"
                                value={editingValue}
                                onChange={(e) => setEditingValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleSaveEdit();
                                  } else if (e.key === 'Escape') {
                                    handleCancelEdit();
                                  }
                                }}
                                className="text-xs px-1 py-0.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                autoFocus
                              />
                              <button
                                onClick={handleSaveEdit}
                                className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300"
                                title="Guardar"
                              >
                                ✓
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                                title="Cancelar"
                              >
                                ✗
                              </button>
                            </div>
                          ) : (
                            <div 
                              className="text-xs text-gray-900 dark:text-white cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 px-1 py-0.5 rounded"
                              onClick={() => handleStartEdit(entity.id, entity.type, 'delivery', entity.delivery_date)}
                              title="Hacer clic para editar"
                            >
                              {format(entity.deliveryDate, 'dd/MM')}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap">
                          {editingEntity?.id === entity.id && editingEntity?.field === 'finish' ? (
                            <div className="flex items-center gap-1">
                              <input
                                type="date"
                                value={editingValue}
                                onChange={(e) => setEditingValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleSaveEdit();
                                  } else if (e.key === 'Escape') {
                                    handleCancelEdit();
                                  }
                                }}
                                className="text-xs px-1 py-0.5 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                autoFocus
                              />
                              <button
                                onClick={handleSaveEdit}
                                className="text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300"
                                title="Guardar"
                              >
                                ✓
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                                title="Cancelar"
                              >
                                ✗
                              </button>
                            </div>
                          ) : (
                            <div 
                              className={`text-xs cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 px-1 py-0.5 rounded ${
                                entity.finishedAt && entity.finishedAt > entity.deliveryDate 
                                  ? 'text-red-600 dark:text-red-400 font-medium' 
                                  : entity.finishedAt
                                  ? 'text-gray-900 dark:text-white'
                                  : 'text-gray-400 dark:text-gray-500'
                              }`}
                              onClick={() => handleStartEdit(entity.id, entity.type, 'finish', entity.finished_at ? entity.finished_at.split('T')[0] : '')}
                              title="Hacer clic para editar"
                            >
                              {entity.finishedAt ? format(entity.finishedAt, 'dd/MM') : '-'}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            entity.status === 'finished' || entity.status === 'shipped' || entity.status === 'shipped/despatched'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
                              : entity.status === 'in_progress'
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200'
                              : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200'
                          }`}>
                            {entity.status === 'finished' ? 'Terminado' : 
                             entity.status === 'shipped' || entity.status === 'shipped/despatched' ? 'Enviado' :
                             entity.status === 'in_progress' ? 'En Proceso' : 'Pendiente'}
                          </span>
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap">
                          <div className="text-xs text-gray-900 dark:text-white">
                            {entity.daysDifference !== null ? (
                              <span className={`font-medium ${
                                entity.daysDifference <= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                              }`}>
                                {entity.daysDifference === 0 ? '0 días' : 
                                 entity.daysDifference < 0 ? `${Math.abs(entity.daysDifference)} días antes` :
                                 `${entity.daysDifference} días tarde`}
                              </span>
                            ) : '-'}
                          </div>
                        </td>
                        <td className="px-2 py-2 whitespace-nowrap">
                          <div className="flex items-center">
                            {entity.isFinished ? (
                              entity.isOnTime ? (
                                <div className="flex items-center text-green-600 dark:text-green-400">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  <span className="text-xs font-medium">✓</span>
                                </div>
                              ) : (
                                <div className="flex items-center text-red-600 dark:text-red-400">
                                  <AlertTriangle className="h-3 w-3 mr-1" />
                                  <span className="text-xs font-medium">✗</span>
                                </div>
                              )
                            ) : (
                              <div className="flex items-center text-amber-600 dark:text-amber-400">
                                <Clock className="h-3 w-3 mr-1" />
                                <span className="text-xs font-medium">⏳</span>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {getModalEntitiesByType.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-lg font-medium">No hay {title.toLowerCase()} en este período</p>
                  <p className="text-sm">Selecciona un período diferente para ver más resultados</p>
                </div>
              )}
            </div>

            <div className="mt-6 text-center">
              <Button
                variant="outline"
                onClick={() => setIsDeliveryComplianceModalOpen(false)}
                className="px-6 py-2"
              >
                Cerrar
              </Button>
            </div>
          </div>
              </>
            );
          })()}
        </div>
      </div>
    )}

    {/* Modal de selección de fecha de terminación */}
    {isFinishDateModalOpen && entityToFinish && (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4" 
        onClick={() => setIsFinishDateModalOpen(false)}
      >
        <div 
          className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full" 
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                <CheckCircle className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Marcar como Terminado
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {entityToFinish.name}
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsFinishDateModalOpen(false)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="h-5 w-5 text-gray-500 dark:text-gray-400" />
            </button>
          </div>

          {/* Contenido */}
          <div className="p-6">
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Fecha de terminación
              </label>
              <input
                type="date"
                value={finishDate}
                onChange={(e) => setFinishDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 dark:bg-gray-700 dark:text-white"
              />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Selecciona la fecha real en que se terminó esta {entityToFinish.type === 'production' ? 'producción' : 'muestra'}. 
                </p>
            </div>

            {/* Botones de acción rápida */}
            <div className="mb-6">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Fechas rápidas:
              </p>
              <div className="grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  onClick={() => setFinishDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])}
                  className="text-xs h-8"
                >
                  Hace 7 días
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setFinishDate(new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0])}
                  className="text-xs h-8"
                >
                  Hace 3 días
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setFinishDate(new Date().toISOString().split('T')[0])}
                  className="text-xs h-8"
                >
                  Hoy
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setFinishDate(new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0])}
                  className="text-xs h-8"
                >
                  Mañana
                </Button>
              </div>
            </div>

            {/* Botones de acción */}
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setIsFinishDateModalOpen(false)}
                className="px-4 py-2"
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmFinish}
                disabled={isMarkingAsFinished || !finishDate}
                className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2"
              >
                {isMarkingAsFinished ? 'Marcando...' : 'Confirmar Terminación'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    )}

    {/* Change History Modal */}
    {selectedEntity && (
      <ChangeHistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        entityType={entityType}
        entityId={selectedEntityId!}
      />
    )}

    </div>
  );
};

export default ProductionManager;